import React from "react";
import { Check } from "lucide-react";
import { Pill } from "./Pill";

/* Typed renderers for the structured resultJson each agent check emits.
   Shapes follow the output contracts declared in checks.ts / the backend
   prompt templates. Unknown checks fall back to a generic key/value view. */

type Rec = Record<string, unknown>;

const STATUS_COLOR: Record<string, string> = {
  open: "--amber", outstanding: "--amber", requested: "--amber", instructed: "--amber",
  pending: "--amber", escalated: "--red", blocked: "--red", high: "--red",
  resolved: "--green", satisfied: "--green", signed: "--green", provided: "--green",
  confirmed: "--green", received: "--green", low: "--green",
  waived: "--blue", released: "--blue", medium: "--blue", milestone: "--blue",
  deadline: "--amber", event: "--low",
};
function statusColor(s: unknown): string {
  return STATUS_COLOR[String(s || "").toLowerCase()] || "--low";
}
function arr(result: Rec | null | undefined, key: string): Rec[] {
  const v = result && (result as Rec)[key];
  return Array.isArray(v) ? (v.filter((x) => x && typeof x === "object") as Rec[]) : [];
}
function str(v: unknown): string {
  return v == null ? "" : typeof v === "string" ? v : JSON.stringify(v);
}

function timelineDateKey(value: unknown): number {
  const text = str(value).trim();
  if (!text) return Number.POSITIVE_INFINITY;
  /* Prefer an embedded ISO date (including labels such as
     "week of 2026-05-04"); fall back to other explicit date forms. */
  const iso = text.match(/\b(\d{4})-(\d{2})-(\d{2})\b/);
  const parsed = Date.parse(iso ? `${iso[1]}-${iso[2]}-${iso[3]}T00:00:00Z` : text);
  return Number.isFinite(parsed) ? parsed : Number.POSITIVE_INFINITY;
}

const rowStyle: React.CSSProperties = {
  display: "flex", alignItems: "baseline", gap: 7, flexWrap: "wrap",
  fontSize: 12, lineHeight: 1.5, padding: "3px 0",
  borderTop: "1px solid var(--bd)",
};

function Row({ children }: { children: React.ReactNode }): JSX.Element {
  return <div style={rowStyle}>{children}</div>;
}
function Sub({ children }: { children: React.ReactNode }): JSX.Element {
  return (
    <span className="tert" style={{ fontSize: 11.5, flexBasis: "100%" }}>
      {children}
    </span>
  );
}

/* ---- per-agent renderers ---- */

export function Timeline({ r }: { r: Rec }): JSX.Element | null {
  const entries = arr(r, "entries")
    .map((entry, index) => ({ entry, index }))
    .sort((a, b) => {
      const byDate = timelineDateKey(a.entry.date) - timelineDateKey(b.entry.date);
      return Number.isNaN(byDate) || byDate === 0 ? a.index - b.index : byDate;
    })
    .map(({ entry }) => entry);
  if (!entries.length) return null;
  return (
    <div>
      {entries.map((e, i) => (
        <Row key={i}>
          <span className="mono" style={{ fontSize: 11, color: "var(--muted)", flex: "none" }}>
            {str(e.date) || "undated"}
          </span>
          <span style={{ flex: 1, minWidth: 120 }}>{str(e.event)}</span>
          {e.kind ? <Pill color={statusColor(e.kind)} label={str(e.kind)} /> : null}
        </Row>
      ))}
    </div>
  );
}

export function Issues({ r }: { r: Rec }): JSX.Element | null {
  const issues = arr(r, "issues");
  if (!issues.length) return null;
  return (
    <div>
      {issues.map((it, i) => (
        <Row key={i}>
          <Pill color={statusColor(it.status)} label={str(it.status) || "open"} />
          <span style={{ flex: 1, minWidth: 120 }}>{str(it.issue)}</span>
          {it.owner ? <span className="tert" style={{ fontSize: 11 }}>owner: {str(it.owner)}</span> : null}
        </Row>
      ))}
    </div>
  );
}

export function Closing({ r }: { r: Rec }): JSX.Element | null {
  const items = arr(r, "items");
  if (!items.length) return null;
  return (
    <div>
      {items.map((it, i) => (
        <Row key={i}>
          <Pill color={statusColor(it.status)} label={str(it.status) || "open"} />
          <span style={{ flex: 1, minWidth: 120 }}>{str(it.condition)}</span>
          {it.evidence ? <Sub>“{str(it.evidence)}”</Sub> : null}
        </Row>
      ))}
    </div>
  );
}

export function Signature({ r }: { r: Rec }): JSX.Element | null {
  const sigs = arr(r, "signatures");
  if (!sigs.length) return null;
  return (
    <div>
      {sigs.map((s, i) => (
        <Row key={i}>
          <span className="mono" style={{ fontSize: 11, color: "var(--fg)", flex: "none" }}>
            {str(s.document)}
          </span>
          <span style={{ flex: 1, minWidth: 100 }}>{str(s.party)}</span>
          <Pill color={statusColor(s.status)} label={str(s.status) || "outstanding"} />
        </Row>
      ))}
    </div>
  );
}

export function FundsFlow({ r }: { r: Rec }): JSX.Element | null {
  const wires = arr(r, "wires");
  const disc = arr(r, "discrepancies");
  if (!wires.length && !disc.length) return null;
  return (
    <div>
      {wires.map((w, i) => (
        <Row key={"w" + i}>
          <span className="mono" style={{ fontSize: 11.5, color: "var(--fg)", flex: "none" }}>
            {str(w.amount)}
          </span>
          <span style={{ flex: 1, minWidth: 120 }}>
            {str(w.from)} → {str(w.to)}
          </span>
          <Pill color={statusColor(w.status)} label={str(w.status) || "instructed"} />
        </Row>
      ))}
      {disc.map((d, i) => (
        <Row key={"d" + i}>
          <Pill color="--red" label={str(d.kind) || "discrepancy"} />
          <span style={{ flex: 1, minWidth: 120, color: "var(--red)" }}>{str(d.detail)}</span>
        </Row>
      ))}
    </div>
  );
}

function Dispatch({ r }: { r: Rec }): JSX.Element | null {
  const actions = arr(r, "actions");
  if (!actions.length) return null;
  return (
    <div>
      {actions.map((a, i) => (
        <Row key={i}>
          <Pill color="--blue" label={str(a.action) || "action"} />
          <span className="mono" style={{ fontSize: 11, color: "var(--fg)" }}>{str(a.document)}</span>
          {a.owner ? <span className="tert" style={{ fontSize: 11 }}>→ {str(a.owner)}</span> : null}
          {a.reason ? <Sub>{str(a.reason)}</Sub> : null}
        </Row>
      ))}
    </div>
  );
}

function DocChange({ r }: { r: Rec }): JSX.Element | null {
  const edits = arr(r, "edits");
  if (!edits.length) return null;
  return (
    <div>
      {edits.map((e, i) => (
        <Row key={i}>
          <span className="mono" style={{ fontSize: 11, color: "var(--fg)", flex: "none" }}>
            {str(e.document)}{e.section ? ` · ${str(e.section)}` : ""}
          </span>
          <span style={{ flex: 1, minWidth: 140 }}>{str(e.change)}</span>
          {e.basis ? <Sub>basis: {str(e.basis)}</Sub> : null}
        </Row>
      ))}
    </div>
  );
}

export function Commitments({ r }: { r: Rec }): JSX.Element | null {
  const cs = arr(r, "commitments");
  if (!cs.length) return null;
  return (
    <div>
      {cs.map((c, i) => (
        <Row key={i}>
          <Pill
            color={String(c.direction) === "owed_by_us" ? "--amber" : "--blue"}
            label={String(c.direction) === "owed_by_us" ? "we owe" : "waiting on"}
          />
          <span style={{ flex: 1, minWidth: 140 }}>
            <strong style={{ fontWeight: 500 }}>{str(c.who)}</strong> — {str(c.what)}
          </span>
          {c.due ? (
            <span className="mono tert" style={{ fontSize: 11 }}>due {str(c.due)}</span>
          ) : null}
        </Row>
      ))}
    </div>
  );
}

export function Diligence({ r }: { r: Rec }): JSX.Element | null {
  const reqs = arr(r, "requests");
  if (!reqs.length) return null;
  return (
    <div>
      {reqs.map((q, i) => (
        <Row key={i}>
          <Pill color={statusColor(q.status)} label={str(q.status) || "requested"} />
          <span style={{ flex: 1, minWidth: 120 }}>{str(q.item)}</span>
          {q.party ? <span className="tert" style={{ fontSize: 11 }}>{str(q.party)}</span> : null}
        </Row>
      ))}
    </div>
  );
}

function Fidelity({ r }: { r: Rec }): JSX.Element | null {
  const ms = arr(r, "mismatches");
  if (!ms.length) return null;
  return (
    <div>
      {ms.map((m, i) => (
        <Row key={i}>
          <span style={{ fontWeight: 500, flex: "none" }}>{str(m.field)}</span>
          <span className="mono" style={{ fontSize: 11 }}>
            memo: {str(m.memoValue)} <span style={{ color: "var(--red)" }}>≠</span> doc: {str(m.docValue)}
          </span>
          {m.document ? <span className="tert" style={{ fontSize: 11 }}>{str(m.document)}</span> : null}
        </Row>
      ))}
    </div>
  );
}

function Discrepancy({ r }: { r: Rec }): JSX.Element | null {
  const ds = arr(r, "discrepancies");
  if (!ds.length) return null;
  return (
    <div>
      {ds.map((d, i) => (
        <Row key={i}>
          {d.severity ? <Pill color={statusColor(d.severity)} label={str(d.severity)} /> : null}
          <span style={{ fontWeight: 500, flex: "none" }}>{str(d.field)}</span>
          <span className="mono" style={{ fontSize: 11 }}>
            memo: {str(d.memoValue)} <span style={{ color: "var(--red)" }}>≠</span> stated: {str(d.statedValue)}
          </span>
          {d.speaker ? <Sub>per {str(d.speaker)}</Sub> : null}
        </Row>
      ))}
    </div>
  );
}

const MOMENTUM_COLOR: Record<string, string> = {
  positive: "--green", steady: "--blue", stalled: "--amber", concerning: "--red",
};
const TRACKING_COLOR: Record<string, string> = {
  on_track: "--green", at_risk: "--amber", behind: "--red",
};
const TRACKING_LABEL: Record<string, string> = {
  on_track: "on track", at_risk: "at risk", behind: "behind",
};

/* Living state-of-play dashboard card (stage, momentum, recent activity,
   tracking, next actions). Still renders the legacy keyRisks/nextSteps
   shape so pre-baked corpus results display. */
export function StateOfPlayCard({ result }: { result: Rec | null | undefined }): JSX.Element | null {
  if (!result) return null;
  const briefing = str(result.briefing);
  const momentum = str(result.momentum).toLowerCase();
  const tracking = str(result.tracking).toLowerCase();
  const activity = (Array.isArray(result.recentActivity) ? result.recentActivity : [])
    .map(str)
    .filter(Boolean);
  const actions = (Array.isArray(result.nextActions) ? result.nextActions : []).filter(
    (a) => a && typeof a === "object",
  ) as Rec[];
  /* legacy shape */
  const risks = (Array.isArray(result.keyRisks) ? result.keyRisks : []).map(str).filter(Boolean);
  const steps = (Array.isArray(result.nextSteps) ? result.nextSteps : []).map(str).filter(Boolean);
  if (!briefing && !activity.length && !actions.length && !risks.length && !steps.length)
    return null;
  const section: React.CSSProperties = { marginTop: 8 };
  const sectionHdr: React.CSSProperties = { fontSize: 10, opacity: 0.7, marginBottom: 3 };
  return (
    <div
      style={{
        background: "var(--subtle)", border: "1px solid var(--bd)", borderRadius: 6,
        padding: "10px 12px", marginBottom: 12,
      }}
    >
      <div
        className="up"
        style={{ marginBottom: 6, display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}
      >
        State of play
        {result.phase ? <Pill color="--blue" label={str(result.phase)} /> : null}
        {momentum ? (
          <Pill color={MOMENTUM_COLOR[momentum] || "--low"} label={`momentum: ${momentum}`} />
        ) : null}
        {tracking ? (
          <Pill
            color={TRACKING_COLOR[tracking] || "--low"}
            label={TRACKING_LABEL[tracking] || tracking}
          />
        ) : null}
      </div>
      {briefing && <div style={{ fontSize: 12.5, lineHeight: 1.55 }}>{briefing}</div>}
      {!!(str(result.momentumNote) || str(result.trackingNote)) && (
        <div className="tert" style={{ fontSize: 11.5, lineHeight: 1.5, marginTop: 4 }}>
          {[str(result.momentumNote), str(result.trackingNote)].filter(Boolean).join(" ")}
        </div>
      )}
      {activity.length > 0 && (
        <div style={section}>
          <div className="up" style={sectionHdr}>Recent activity</div>
          {activity.map((x, i) => (
            <div key={i} style={{ fontSize: 12, lineHeight: 1.5 }}>· {x}</div>
          ))}
        </div>
      )}
      {actions.length > 0 && (
        <div style={section}>
          <div className="up" style={sectionHdr}>Next actions</div>
          {actions.map((a, i) => (
            <div key={i} style={{ fontSize: 12, lineHeight: 1.5 }}>
              · {str(a.action)}
              {a.who ? <span className="tert"> — {str(a.who)}</span> : null}
            </div>
          ))}
        </div>
      )}
      {risks.length > 0 && (
        <div style={section}>
          <div className="up" style={sectionHdr}>Key risks</div>
          {risks.map((x, i) => (
            <div key={i} style={{ fontSize: 12, lineHeight: 1.5, color: "var(--red)" }}>· {x}</div>
          ))}
        </div>
      )}
      {steps.length > 0 && (
        <div style={section}>
          <div className="up" style={sectionHdr}>Next steps</div>
          {steps.map((x, i) => (
            <div key={i} style={{ fontSize: 12, lineHeight: 1.5 }}>· {x}</div>
          ))}
        </div>
      )}
    </div>
  );
}

/* Participant Resolution detail — the mapped/unknown lists behind the
   "N mapped, M unknown" summary. entityNames resolves entityId → display name. */
export function ParticipantMap({
  result,
  entityNames,
}: {
  result: Rec | null | undefined;
  entityNames?: Record<string, string>;
}): JSX.Element | null {
  if (!result) return null;
  const mapped = arr(result, "mapped");
  const unknown = (Array.isArray(result.unknown) ? result.unknown : [])
    .map(str)
    .filter(Boolean);
  if (!mapped.length && !unknown.length) return null;
  return (
    <div>
      {mapped.map((m, i) => {
        const eid = str(m.entityId);
        const name = (entityNames && entityNames[eid]) || eid || "unmatched entity";
        return (
          <Row key={i}>
            <span className="mono" style={{ fontSize: 11, color: "var(--muted)", flex: "none" }}>
              {str(m.email)}
            </span>
            <span style={{ color: "var(--tert)", flex: "none" }}>→</span>
            <span style={{ flex: 1, minWidth: 100 }}>
              {name}
              {m.role ? <span className="tert"> ({str(m.role)})</span> : null}
            </span>
            {m.confirmed === false ? (
              <span className="tert" style={{ fontSize: 10.5 }}>new</span>
            ) : m.confirmed ? (
              <Check size={11} style={{ color: "var(--green)", flex: "none" }} />
            ) : null}
          </Row>
        );
      })}
      {unknown.length > 0 && (
        <div style={{ marginTop: 6 }}>
          <div className="up" style={{ fontSize: 10, marginBottom: 2 }}>
            Unknown
          </div>
          {unknown.map((u, i) => (
            <div key={i} className="mono tert" style={{ fontSize: 11, lineHeight: 1.6 }}>
              {u}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/* Generic fallback: render any non-housekeeping keys as key/value rows;
   arrays of objects become one row per element. */
const SKIP_KEYS = new Set(["conf", "out", "ok", "rationale"]);
function Generic({ r }: { r: Rec }): JSX.Element | null {
  const keys = Object.keys(r).filter((k) => !SKIP_KEYS.has(k));
  const parts: JSX.Element[] = [];
  keys.forEach((k) => {
    const v = r[k];
    if (v == null || (Array.isArray(v) && !v.length)) return;
    if (Array.isArray(v)) {
      v.forEach((el, i) => {
        parts.push(
          <Row key={k + i}>
            <span className="tert" style={{ fontSize: 11, flex: "none" }}>{k}</span>
            <span style={{ flex: 1, minWidth: 120 }}>
              {typeof el === "object" && el
                ? Object.entries(el as Rec).map(([ek, ev]) => `${ek}: ${str(ev)}`).join(" · ")
                : str(el)}
            </span>
          </Row>,
        );
      });
    } else {
      parts.push(
        <Row key={k}>
          <span className="tert" style={{ fontSize: 11, flex: "none" }}>{k}</span>
          <span style={{ flex: 1, minWidth: 120 }}>{str(v)}</span>
        </Row>,
      );
    }
  });
  return parts.length ? <div>{parts}</div> : null;
}

const RENDERERS: Record<string, (p: { r: Rec }) => JSX.Element | null> = {
  timeline: Timeline,
  issues: Issues,
  closing: Closing,
  signature: Signature,
  fundsflow: FundsFlow,
  dispatch: Dispatch,
  docchange: DocChange,
  commitments: Commitments,
  diligence: Diligence,
  fidelity: Fidelity,
  discrepancy: Discrepancy,
};

export function AgentResult({
  id,
  result,
}: {
  id: string;
  result: Record<string, unknown> | null | undefined;
}): JSX.Element | null {
  if (!result || typeof result !== "object") return null;
  if (id === "stateofplay") return <StateOfPlayCard result={result as Rec} />;
  const R = RENDERERS[id] || Generic;
  return <R r={result as Rec} />;
}
